import { toast } from 'react-toastify';
import websocketService from './websocketService';
import api from '../utils/api';

const { notificationAPI } = api;

class NotificationService {
  constructor() {
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners = new Map();
    this.initialized = false;
  }

  // Initialize notification service
  async initialize(adminId, adminName, companyId) {
    const wasInitialized = this.initialized;
    this.adminId = adminId;
    this.adminName = adminName;
    this.companyId = companyId;

    if (wasInitialized) {
      await this.loadNotifications();
      return;
    }

    // Subscribe to unified personnel notification event from backend
    websocketService.on('personnel-notification-individual', this.handlePersonnelNotification.bind(this));

    // Keep legacy event handlers for backwards compatibility
    websocketService.on('personnel-added', this.handlePersonnelAdded.bind(this));
    websocketService.on('personnel-updated', this.handlePersonnelUpdated.bind(this));
    websocketService.on('personnel-disabled', this.handlePersonnelDisabled.bind(this));
    websocketService.on('personnel-deleted', this.handlePersonnelDeleted.bind(this));
    websocketService.on('admin-notification', this.handleAdminNotification.bind(this));

    // Site management notification events
    websocketService.on('site-notification-individual', this.handleSiteNotification.bind(this));
    websocketService.on('site-added', this.handleSiteAdded.bind(this));
    websocketService.on('site-updated', this.handleSiteUpdated.bind(this));
    websocketService.on('site-deleted', this.handleSiteDeleted.bind(this));

    // Structure management notification events
    websocketService.on('structure-notification-individual', this.handleStructureNotification.bind(this));
    websocketService.on('branch-added', this.handleBranchAdded.bind(this));
    websocketService.on('region-added', this.handleRegionAdded.bind(this));
    websocketService.on('site-assigned', this.handleSiteAssigned.bind(this));

    // Coverage notification events
    websocketService.on('coverage-notification-individual', this.handleCoverageNotification.bind(this));
    websocketService.on('coverage-submitted', this.handleCoverageSubmitted.bind(this));
    websocketService.on('coverage-edited', this.handleCoverageEdited.bind(this));

    // Client notification events
    websocketService.on('client-notification-individual', this.handleClientNotification.bind(this));
    websocketService.on('client-added', this.handleClientAdded.bind(this));

    // Incident report notification events
    websocketService.on('incident-notification-individual', this.handleIncidentNotification.bind(this));
    websocketService.on('incident-submitted', this.handleIncidentSubmitted.bind(this));
    websocketService.on('incident-status-updated', this.handleIncidentStatusUpdated.bind(this));

    // Special assignment notification events
    websocketService.on('assignment-notification-individual', this.handleAssignmentNotification.bind(this));
    websocketService.on('assignment-submitted', this.handleAssignmentSubmitted.bind(this));
    websocketService.on('assignment-assigned', this.handleAssignmentAssigned.bind(this));
    websocketService.on('assignment-deleted', this.handleAssignmentDeleted.bind(this));
    websocketService.on('assignment-approved', this.handleAssignmentApproved.bind(this));
    websocketService.on('assignment-accepted', this.handleAssignmentAccepted.bind(this));
    websocketService.on('assignment-rejected', this.handleAssignmentRejected.bind(this));
    websocketService.on('assignment-completed', this.handleAssignmentCompleted.bind(this));

    this.initialized = true;
    
    // Load existing notifications from backend
    await this.loadNotifications();
  }

  // Load notifications from backend API
  async loadNotifications() {
    try {
      const response = await notificationAPI.getAll({ limit: 50 });
      
      let notifications = [];
      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          notifications = response.data.data;
        } else if (Array.isArray(response.data)) {
          notifications = response.data;
        } else if (response.data.notifications && Array.isArray(response.data.notifications)) {
          notifications = response.data.notifications;
        }
      }
      
      this.setNotifications(notifications);
    } catch (error) {
      // Continue with empty notifications rather than failing
    }
  }

  // Handle unified personnel notification from backend
  handlePersonnelNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first — covers both actor and non-actor paths.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) {
        // Already in the bell (from a previous delivery). Nothing to do.
        return;
      }
      // Local-only entry was added by triggerPersonnelAction but didn't get a backendId yet.
      // Find it by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notification.type || 'general',
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Helper function to format admin name
  formatAdminName(adminName) {
    if (!adminName) return 'An admin';
    
    if (adminName.toLowerCase().startsWith('admin') || 
        adminName.toLowerCase().startsWith('manager') ||
        adminName.toLowerCase().startsWith('supervisor')) {
      return adminName;
    }
    
    return `Admin ${adminName}`;
  }

  // Handle personnel added notification
  handlePersonnelAdded(data) {
    const { adminName, personnelName, adminId, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'personnel-added',
      message: `${this.formatAdminName(adminName)} added personnel ${personnelName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle personnel updated notification
  handlePersonnelUpdated(data) {
    const { adminName, personnelName, adminId, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'personnel-updated',
      message: `${this.formatAdminName(adminName)} updated personnel ${personnelName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle personnel disabled notification
  handlePersonnelDisabled(data) {
    const { adminName, personnelName, adminId, timestamp, isDisabled } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const action = isDisabled ? 'disabled' : 'enabled';
    const notification = {
      id: Date.now(),
      type: 'personnel-disabled',
      message: `${this.formatAdminName(adminName)} ${action} personnel ${personnelName}'s account.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle personnel deleted notification
  handlePersonnelDeleted(data) {
    const { adminName, personnelName, adminId, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'personnel-deleted',
      message: `${this.formatAdminName(adminName)} deleted personnel ${personnelName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle unified site notification from backend
  handleSiteNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) return;
      // Find the local-only entry by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notification.type || 'site',
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Handle site added notification
  handleSiteAdded(data) {
    const { adminName, siteName, adminId, timestamp } = data;
    if (adminId === this.adminId) return;
    const notification = {
      id: Date.now(),
      type: 'site-added',
      message: `${this.formatAdminName(adminName)} added site ${siteName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };
    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle site updated notification
  handleSiteUpdated(data) {
    const { adminName, siteName, adminId, timestamp } = data;
    if (adminId === this.adminId) return;
    const notification = {
      id: Date.now(),
      type: 'site-updated',
      message: `${this.formatAdminName(adminName)} updated site ${siteName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };
    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle site deleted notification
  handleSiteDeleted(data) {
    const { adminName, siteName, adminId, timestamp } = data;
    if (adminId === this.adminId) return;
    const notification = {
      id: Date.now(),
      type: 'site-deleted',
      message: `${this.formatAdminName(adminName)} deleted site ${siteName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };
    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle generic admin notification
  handleAdminNotification(data) {
    const notification = {
      id: Date.now(),
      type: data.type || 'general',
      message: data.message,
      timestamp: data.timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: data.adminId === this.adminId
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Add notification to list
  addNotification(notification) {
    this.notifications.unshift(notification);
    if (!notification.read && !notification.isOwnAction) {
      this.unreadCount++;
    }
    this.emit('notification-added', notification);
    this.emit('unread-count-changed', this.unreadCount);
  }

  // Show toast notification
  showToast(notification) {
    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    if (notification.isOwnAction) {
      toast.success(notification.message, toastOptions);
    } else {
      toast.info(notification.message, toastOptions);
    }
  }

  // Get all notifications
  getNotifications() {
    return this.notifications;
  }

  // Get unread count
  getUnreadCount() {
    return this.unreadCount;
  }

  // Mark notification as read
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      notification.read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      this.emit('notification-read', notificationId);
      this.emit('unread-count-changed', this.unreadCount);
    }
  }

  // Mark all notifications as read
  markAllAsRead() {
    this.notifications.forEach(notification => {
      notification.read = true;
    });
    this.unreadCount = 0;
    this.emit('all-notifications-read');
    this.emit('unread-count-changed', this.unreadCount);
  }

  // Set notifications from API
  setNotifications(notifications) {
    if (notifications.length === 0 && this.notifications.length > 0) {
      return;
    }
    
    const backendNotifications = notifications.map(n => {
      // Check all common field names the backend might use for the actor's ID
      let isOwnAction =
        n.adminId === this.adminId ||
        n.userId === this.adminId ||
        n.actorId === this.adminId ||
        n.createdById === this.adminId;

      if (!isOwnAction && n.message) {
        isOwnAction = n.message.trim().startsWith('You have');
      }
      
      const isMarkedRead = n.read || n.isRead || false;
      
      return {
        id: n.id ? `${n.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : Date.now(),
        backendId: n.id,
        type: n.type || 'general',
        message: n.message,
        timestamp: n.timestamp || n.createdAt || new Date().toISOString(),
        read: isOwnAction ? true : isMarkedRead,
        isOwnAction: isOwnAction
      };
    });
    
    const backendIds = new Set(backendNotifications.map(n => n.backendId));
    const websocketNotifications = this.notifications.filter(n => !backendIds.has(n.backendId));
    this.notifications = [...websocketNotifications, ...backendNotifications];
    
    this.unreadCount = this.notifications.filter(n => !n.read).length;
    this.emit('notifications-loaded', this.notifications);
    this.emit('unread-count-changed', this.unreadCount);
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Emit event to listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          // Silent error handling in production
        }
      });
    }
  }

  // Clear all notifications
  clear() {
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners.clear();
    this.initialized = false;
    this.adminId = null;
    this.adminName = null;
  }

  // Trigger personnel action notification
  triggerPersonnelAction(action, personnelName) {
    const data = {
      action,
      personnelName,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'personnel-added': `Personnel ${personnelName} has been added successfully!`,
      'personnel-updated': `Personnel ${personnelName} has been updated successfully!`,
      'personnel-disabled': `Personnel ${personnelName}'s account status has been changed successfully!`,
      'personnel-deleted': `Personnel ${personnelName} has been deleted successfully!`
    };

    const message = actionMessages[action] || `Personnel action completed successfully!`;

    // Add immediately to the bell for the acting admin
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: data.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitPersonnelAction(action, data);
  }

  // Trigger site action notification
  triggerSiteAction(action, siteName) {
    const data = {
      action,
      siteName,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'site-added':   `Site ${siteName} has been added successfully!`,
      'site-updated': `Site ${siteName} has been updated successfully!`,
      'site-deleted': `Site ${siteName} has been deleted successfully!`
    };

    const message = actionMessages[action] || `Site action completed successfully!`;

    // Add immediately to the bell for the acting admin
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: data.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitSiteAction(action, data);
  }

  // Handle unified structure notification from backend
  handleStructureNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) return;
      // Find the local-only entry by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notification.type || 'structure',
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Handle branch added notification (legacy)
  handleBranchAdded(data) {
    const { adminName, branchName, adminId, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'branch-added',
      message: `${this.formatAdminName(adminName)} added branch ${branchName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle region added notification (legacy)
  handleRegionAdded(data) {
    const { adminName, regionName, branchName, adminId, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'region-added',
      message: `${this.formatAdminName(adminName)} added region ${regionName} to branch ${branchName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle site assigned notification (legacy)
  handleSiteAssigned(data) {
    const { adminName, regionName, siteCount, adminId, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'site-assigned',
      message: `${this.formatAdminName(adminName)} assigned ${siteCount} site${siteCount !== 1 ? 's' : ''} to region ${regionName}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Trigger structure action notification
  triggerStructureAction(action, entityName, extraData = {}) {
    const data = {
      action,
      entityName,
      ...extraData,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'branch-added': `Branch ${entityName} has been added successfully!`,
      'region-added': `Region ${entityName} has been added successfully!`,
      'site-assigned': `Sites have been assigned successfully!`,
      'branch-deleted': `Branch ${entityName} has been deleted successfully!`,
      'region-deleted': `Region ${entityName} has been deleted successfully!`
    };

    const message = actionMessages[action] || `Structure action completed successfully!`;

    // Add immediately to the bell for the acting admin
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: data.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitStructureAction(action, data);
  }

  // Handle unified coverage notification from backend
  handleCoverageNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) return;
      // Find the local-only entry by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Convert underscore to hyphen for compatibility with frontend mappings
    let notificationType = notification.type || 'coverage';
    if (notificationType === 'coverage_submitted') {
      notificationType = 'coverage-submitted';
    } else if (notificationType === 'coverage_edited') {
      notificationType = 'coverage-edited';
    }
    
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notificationType,
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Handle coverage submitted notification (legacy)
  handleCoverageSubmitted(data) {
    const { personnelName, personnelId, siteName, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'coverage-submitted',
      message: `Personnel ${personnelName} has submitted a coverage${siteName ? ` for ${siteName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle coverage edited notification (legacy)
  handleCoverageEdited(data) {
    const { adminName, adminId, personnelName, siteName, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'coverage-edited',
      message: `${this.formatAdminName(adminName)} edited a coverage${personnelName ? ` submitted by ${personnelName}` : ''}${siteName ? ` for ${siteName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Trigger coverage action notification
  triggerCoverageAction(action, data) {
    const payload = {
      action,
      ...data,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'coverage-submitted': `Coverage ${data.siteName ? `for ${data.siteName}` : ''} has been submitted successfully!`,
      'coverage-edited': `Coverage ${data.siteName ? `for ${data.siteName}` : ''} has been updated successfully!`
    };

    const message = actionMessages[action] || `Coverage action completed successfully!`;

    // Add immediately to the bell for the acting admin/personnel
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: payload.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitCoverageAction(action, payload);
  }

  // Handle unified client notification from backend
  handleClientNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) return;
      // Find the local-only entry by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Convert underscore to hyphen for compatibility with frontend mappings
    let notificationType = notification.type || 'client';
    if (notificationType === 'client_added') {
      notificationType = 'client-added';
    }
    
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notificationType,
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Handle client added notification (legacy)
  handleClientAdded(data) {
    const { personnelName, personnelId, clientName, siteName, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'client-added',
      message: `Personnel ${personnelName} has added a new client ${clientName}${siteName ? ` for site ${siteName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Trigger client action notification
  triggerClientAction(action, data) {
    const payload = {
      action,
      ...data,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'client-added': `Client ${data.clientName}${data.siteName ? ` for ${data.siteName}` : ''} has been added successfully!`
    };

    const message = actionMessages[action] || `Client action completed successfully!`;

    // Add immediately to the bell for the acting personnel
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: payload.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitClientAction(action, payload);
  }

  // Handle unified incident report notification from backend
  handleIncidentNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) return;
      // Find the local-only entry by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Convert underscore to hyphen for compatibility with frontend mappings
    let notificationType = notification.type || 'incident';
    if (notificationType === 'incident_submitted') {
      notificationType = 'incident-submitted';
    } else if (notificationType === 'incident_status_updated') {
      notificationType = 'incident-status-updated';
    }
    
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notificationType,
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Handle incident submitted notification (legacy)
  handleIncidentSubmitted(data) {
    const { personnelName, personnelId, incidentTitle, siteName, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'incident-submitted',
      message: `Personnel ${personnelName} has submitted an incident report${incidentTitle ? `: ${incidentTitle}` : ''}${siteName ? ` at ${siteName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle incident status updated notification (legacy)
  handleIncidentStatusUpdated(data) {
    const { adminName, adminId, incidentTitle, newStatus, siteName, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const statusLabels = {
      'submitted': 'submitted',
      'under_investigation': 'under investigation',
      'resolved': 'resolved',
      'dismissed': 'dismissed'
    };
    
    const notification = {
      id: Date.now(),
      type: 'incident-status-updated',
      message: `${this.formatAdminName(adminName)} updated incident report${incidentTitle ? ` "${incidentTitle}"` : ''} status to ${statusLabels[newStatus] || newStatus}${siteName ? ` at ${siteName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Trigger incident action notification
  triggerIncidentAction(action, data) {
    const payload = {
      action,
      ...data,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'incident-submitted': `Incident report${data.incidentTitle ? ` "${data.incidentTitle}"` : ''} has been submitted successfully!`,
      'incident-status-updated': `Incident report status has been updated successfully!`
    };

    const message = actionMessages[action] || `Incident action completed successfully!`;

    // Add immediately to the bell for the acting user
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: payload.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitIncidentAction(action, payload);
  }

  // Handle unified special assignment notification from backend
  handleAssignmentNotification(data) {
    const { notification, isActor } = data;
    const backendId = notification.id;

    // Always deduplicate by backendId first.
    const alreadyPresent = this.notifications.some(n => n.backendId === backendId);

    if (isActor) {
      if (alreadyPresent) return;
      // Find the local-only entry by action type + isOwnAction flag (within last 10 seconds).
      const tenSecondsAgo = Date.now() - 10_000;
      const localEntry = this.notifications.find(
        n => n.isOwnAction && !n.backendId &&
             n.localActionType === notification.type &&
             new Date(n.timestamp).getTime() >= tenSecondsAgo
      );
      if (localEntry) {
        localEntry.backendId = backendId;
      }
      return;
    }

    if (alreadyPresent) return;

    const uniqueId = `${backendId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Convert underscore to hyphen for compatibility with frontend mappings
    let notificationType = notification.type || 'assignment';
    if (notificationType === 'assignment_submitted') {
      notificationType = 'assignment-submitted';
    } else if (notificationType === 'assignment_assigned') {
      notificationType = 'assignment-assigned';
    } else if (notificationType === 'assignment_deleted') {
      notificationType = 'assignment-deleted';
    } else if (notificationType === 'assignment_approved') {
      notificationType = 'assignment-approved';
    } else if (notificationType === 'assignment_accepted') {
      notificationType = 'assignment-accepted';
    } else if (notificationType === 'assignment_rejected') {
      notificationType = 'assignment-rejected';
    } else if (notificationType === 'assignment_completed') {
      notificationType = 'assignment-completed';
    }
    
    const notificationObj = {
      id: uniqueId,
      backendId,
      type: notificationType,
      message: notification.message,
      timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
      read: notification.read || notification.isRead || false,
      isOwnAction: false
    };
    this.addNotification(notificationObj);
    this.showToast(notificationObj);
  }

  // Handle assignment submitted notification (legacy)
  handleAssignmentSubmitted(data) {
    const { personnelName, personnelId, assignmentTitle, location, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-submitted',
      message: `Personnel ${personnelName} has submitted a special assignment${assignmentTitle ? `: ${assignmentTitle}` : ''}${location ? ` at ${location}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle assignment assigned notification (legacy)
  handleAssignmentAssigned(data) {
    const { adminName, adminId, personnelName, assignmentTitle, location, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-assigned',
      message: `${this.formatAdminName(adminName)} assigned a special assignment${assignmentTitle ? ` "${assignmentTitle}"` : ''} to ${personnelName}${location ? ` at ${location}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle assignment deleted notification (legacy)
  handleAssignmentDeleted(data) {
    const { adminName, adminId, assignmentTitle, personnelName, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-deleted',
      message: `${this.formatAdminName(adminName)} deleted special assignment${assignmentTitle ? ` "${assignmentTitle}"` : ''}${personnelName ? ` for ${personnelName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle assignment approved notification (legacy)
  handleAssignmentApproved(data) {
    const { adminName, adminId, assignmentTitle, personnelName, timestamp } = data;
    
    if (adminId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-approved',
      message: `${this.formatAdminName(adminName)} approved special assignment${assignmentTitle ? ` "${assignmentTitle}"` : ''}${personnelName ? ` for ${personnelName}` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle assignment accepted notification (legacy)
  handleAssignmentAccepted(data) {
    const { personnelName, personnelId, assignmentTitle, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-accepted',
      message: `Personnel ${personnelName} has accepted special assignment${assignmentTitle ? ` "${assignmentTitle}"` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle assignment rejected notification (legacy)
  handleAssignmentRejected(data) {
    const { personnelName, personnelId, assignmentTitle, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-rejected',
      message: `Personnel ${personnelName} has rejected special assignment${assignmentTitle ? ` "${assignmentTitle}"` : ''}.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Handle assignment completed notification (legacy)
  handleAssignmentCompleted(data) {
    const { personnelName, personnelId, assignmentTitle, timestamp } = data;
    
    if (personnelId === this.adminId) {
      return;
    }
    
    const notification = {
      id: Date.now(),
      type: 'assignment-completed',
      message: `Personnel ${personnelName} has marked special assignment${assignmentTitle ? ` "${assignmentTitle}"` : ''} as completed.`,
      timestamp: timestamp || new Date().toISOString(),
      read: false,
      isOwnAction: false
    };

    this.addNotification(notification);
    this.showToast(notification);
  }

  // Trigger assignment action notification
  triggerAssignmentAction(action, data) {
    const payload = {
      action,
      ...data,
      adminId: this.adminId,
      adminName: this.adminName,
      companyId: this.companyId,
      timestamp: new Date().toISOString()
    };

    const actionMessages = {
      'assignment-submitted': `Special assignment${data.assignmentTitle ? ` "${data.assignmentTitle}"` : ''} has been submitted successfully!`,
      'assignment-assigned': `Special assignment${data.assignmentTitle ? ` "${data.assignmentTitle}"` : ''} has been assigned successfully!`,
      'assignment-deleted': `Special assignment has been deleted successfully!`,
      'assignment-approved': `Special assignment${data.assignmentTitle ? ` "${data.assignmentTitle}"` : ''} has been approved successfully!`,
      'assignment-accepted': `You have accepted special assignment${data.assignmentTitle ? ` "${data.assignmentTitle}"` : ''}.`,
      'assignment-rejected': `You have rejected special assignment${data.assignmentTitle ? ` "${data.assignmentTitle}"` : ''}.`,
      'assignment-completed': `Special assignment${data.assignmentTitle ? ` "${data.assignmentTitle}"` : ''} has been marked as completed!`
    };

    const message = actionMessages[action] || `Assignment action completed successfully!`;

    // Add immediately to the bell for the acting user
    this.addNotification({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: action,
      localActionType: action,
      message,
      timestamp: payload.timestamp,
      read: true,
      isOwnAction: true
    });

    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    toast.success(message, toastOptions);
    websocketService.emitAssignmentAction(action, payload);
  }
}

// Export singleton instance
export default new NotificationService();
